import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiUser,
  FiRefreshCw, FiUsers, FiUserCheck, FiUserX, FiAlertCircle, FiX,
} from 'react-icons/fi';
import { api } from '@crm/shared';
import Layout from '../../components/Layout';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';

/* ─── Design tokens ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyL:'#EBF4FF', navyM:'#1A5276',
  gold:'#F5A623', goldL:'#FEF3DC',
  green:'#16A34A', greenL:'#DCFCE7', greenB:'#86EFAC',
  red:'#DC2626', redL:'#FEF2F2', redB:'#FECACA',
  sky:'#0284C7', skyL:'#DBEAFE',
  purple:'#7C3AED', purpleL:'#EDE9FE',
  border:'#E5E7EB', borderL:'#F3F4F6', bg:'#F4F6F9',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Role badge colors ─── */
const ROLE_COLORS = {
  'Administrator':   { bg: C.redL,    color: C.red,    border: C.redB  },
  'Admin':           { bg: C.redL,    color: C.red,    border: C.redB  },
  'Sales Manager':   { bg: C.skyL,    color: C.sky,    border:'#BAE6FD'},
  'Team Lead':       { bg: C.purpleL, color: C.purple, border:'#C4B5FD'},
  'Sales Executive': { bg: C.greenL,  color: C.green,  border: C.greenB},
  'Accountant':      { bg: C.goldL,   color:'#92670C', border:'#FDE68A'},
  'FOS':             { bg:'#FFF7ED',  color:'#C2410C', border:'#FED7AA'},
};
const ROLE_DEFAULT = { bg: C.navyL, color: C.navy, border:'#BFDBFE' };

const AVATAR_COLORS = [C.navy, C.sky, C.green, '#92670C', C.red, C.purple];

/* ─── Shimmer ─── */
const Sh = ({ h=20, w='100%', r=6 }) => (
  <div style={{ height:h, width:w, borderRadius:r, background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)', backgroundSize:'400px 100%', animation:'cfShimmer 1.4s infinite' }}/>
);

/* ─── Avatar ─── */
function Avatar({ firstName, lastName, image, size=34 }) {
  const initials = `${firstName?.[0]||''}${lastName?.[0]||''}`.toUpperCase() || '?';
  const bg = AVATAR_COLORS[(firstName?.charCodeAt(0)||0) % AVATAR_COLORS.length];
  
  // If image exists, show image
  if (image) {
    return (
      <img 
        src={image} 
        alt={initials}
        style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover', flexShrink:0 }}
      />
    );
  }
  
  // Otherwise show initials
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:bg, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:size*0.36, letterSpacing:'0.03em', flexShrink:0 }}>
      {initials}
    </div>
  );
}

/* ─── Role badge ─── */
function RoleBadge({ roleValue, roleMap }) {
  let name = '';
  if (roleValue && typeof roleValue === 'object') name = roleValue.name || '';
  else if (roleValue) name = roleMap[String(roleValue)] || `Role #${roleValue}`;
  if (!name) return <span style={{ color:C.t4, fontSize:12 }}>—</span>;
  const s = ROLE_COLORS[name] || ROLE_DEFAULT;
  return (
    <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:20, fontSize:11.5, fontWeight:700, background:s.bg, color:s.color, border:`1px solid ${s.border}` }}>
      {name}
    </span>
  );
}

/* ─── Status pill ─── */
function StatusPill({ active }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:20, fontSize:11.5, fontWeight:700, background: active ? C.greenL : C.redL, color: active ? C.green : C.red, border:`1px solid ${active ? C.greenB : C.redB}` }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background: active ? C.green : C.red, display:'inline-block' }}/>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

/* ─── KPI card ─── */
function KpiCard({ label, value, icon, color, colorL, loading }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'14px 16px 12px', transition:'all 0.17s', transform:hov?'translateY(-2px)':'none', boxShadow:hov?'0 8px 22px rgba(13,59,94,0.13)':'0 2px 6px rgba(13,59,94,0.06)', position:'relative', overflow:'hidden', borderTop:`3px solid ${color}` }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
        <span style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em' }}>{label}</span>
        <div style={{ width:32, height:32, borderRadius:9, background:colorL||color+'22', display:'flex', alignItems:'center', justifyContent:'center', color, flexShrink:0 }}>{icon}</div>
      </div>
      {loading ? <Sh h={36} w='45%' r={6}/> : <div style={{ fontSize:30, fontWeight:800, color:C.t1, letterSpacing:'-0.05em', lineHeight:1 }}>{value}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   USERS LIST
═══════════════════════════════════════════════════════ */
export default function UsersList() {
  const navigate = useNavigate();
  const [users,       setUsers]       = useState([]);
  const [roles,       setRoles]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [search,      setSearch]      = useState('');
  const [filterStatus,setFilterStatus]= useState('');
  const [deletingId,  setDeletingId]  = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(null);
  const [togglingId,  setTogglingId]  = useState(null);
  const [apiError,    setApiError]    = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const PAGE_SIZE = 15;

  const roleMap = Object.fromEntries(roles.map(r => [String(r.id), r.name]));

  const fetchAll = useCallback(async (page=1, isRefresh=false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      setApiError(null);
      const [uRes, rRes] = await Promise.all([api.getUsers({ page, page_size: PAGE_SIZE }), api.getRoles()]);
      const ud = uRes?.data;
      if (Array.isArray(ud?.results)) { setUsers(ud.results); setTotalCount(ud.count||0); }
      else if (Array.isArray(ud))     { setUsers(ud);         setTotalCount(ud.length); }
      else                            { setUsers([]);          setTotalCount(0); }
      const rd = rRes?.data;
      setRoles(Array.isArray(rd?.data)?rd.data:Array.isArray(rd?.results)?rd.results:Array.isArray(rd)?rd:[]);
      setCurrentPage(page);
    } catch (err) {
      const s = err?.status||err?.response?.status;
      setApiError(s===403?'Access denied (403). Your account may lack a role.'
                 :s===401?'Session expired — please log in again.'
                 :err?.message||'Failed to load users.');
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetchAll(1); }, [fetchAll]);

  const handleDelete = async (user) => {
    const name = `${user.first_name} ${user.last_name}`.trim() || user.email;
    setDeletingId(user.id);
    setConfirmDel(null);
    try {
      await api.deleteUser(user.id);
      toast.success(`"${name}" deleted`);
      fetchAll(currentPage);
    } catch (err) {
      toast.error(err?.message||'Failed to delete user');
    } finally { setDeletingId(null); }
  };

  const handleToggleActive = async (user) => {
    setTogglingId(user.id);
    try {
      await api.patchUser(user.id, { is_active: !user.is_active });
      const name = `${user.first_name} ${user.last_name}`.trim() || user.email;
      if (user.is_active) { toast.success(`"${name}" deactivated — they can no longer login`); }
      else { toast.success(`"${name}" activated`); }
      fetchAll(currentPage);
    } catch (err) {
      toast.error(err?.message||'Failed to update user status');
    } finally { setTogglingId(null); }
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const roleName = typeof u.role==='object' ? u.role?.name||'' : roleMap[String(u.role)]||'';
    const matchSearch = !search || [u.first_name, u.last_name, u.email, u.mobile_number, u.designation, roleName].some(v => v?.toLowerCase().includes(q));
    const matchStatus = !filterStatus || (filterStatus==='active' ? u.is_active!==false : u.is_active===false);
    return matchSearch && matchStatus;
  });

  const activeCount   = users.filter(u => u.is_active!==false).length;
  const inactiveCount = users.filter(u => u.is_active===false).length;

  return (
    <>
      <style>{`
        @keyframes cfShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes cfSpin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes cfFadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .ul-tr:hover { background:#F7F9FC !important; }
        .ul-icon-btn { width:30px;height:30px;border-radius:8px;border:1.5px solid ${C.border};background:${C.white};display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.13s; }
        .ul-icon-btn:hover { transform:translateY(-1px);box-shadow:0 3px 8px rgba(0,0,0,0.10); }
        .ul-icon-btn:disabled { opacity:0.4;cursor:not-allowed;transform:none; }
      `}</style>
      <Layout pageTitle="User Management">
        <div style={{ display:'flex', flexDirection:'column', gap:14, fontFamily:"'Plus Jakarta Sans',sans-serif", animation:'cfFadeUp 0.22s ease both' }}>

          {/* ── HERO BANNER ── */}
          <div style={{ borderRadius:14, padding:'20px 28px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14, background:'linear-gradient(120deg,#0D3B5E,#1A5276)', boxShadow:'0 4px 20px rgba(13,59,94,0.20)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.55)', marginBottom:4, letterSpacing:'0.06em', textTransform:'uppercase' }}>Management</div>
              <h2 style={{ margin:0, color:'#fff', fontSize:22, fontWeight:800, letterSpacing:'-0.03em', lineHeight:1.2 }}>User Management</h2>
              <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', marginTop:4 }}>Manage system users, roles, and access levels</div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <button onClick={() => fetchAll(currentPage, true)} disabled={refreshing} title="Refresh"
                style={{ width:38, height:38, borderRadius:10, background:'rgba(255,255,255,0.11)', border:'1px solid rgba(255,255,255,0.22)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.12s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.20)';}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.11)';}}>
                <FiRefreshCw size={15} style={{ animation:refreshing?'cfSpin 1s linear infinite':'none' }}/>
              </button>
              <button onClick={() => navigate('/users/create')}
                style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 20px', borderRadius:9, background:C.gold, color:'#0D1B2A', border:'none', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 2px 10px rgba(245,166,35,0.45)', transition:'all 0.12s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='#FBBF4A';e.currentTarget.style.transform='translateY(-1px)';}}
                onMouseLeave={e=>{e.currentTarget.style.background=C.gold;e.currentTarget.style.transform='';}}>
                <FiPlus size={15}/> Add User
              </button>
            </div>
          </div>

          {/* ── KPI CARDS ── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            <KpiCard label="Total Users"    value={totalCount}    icon={<FiUsers size={15}/>}     color={C.navy}  colorL={C.navyL} loading={loading}/>
            <KpiCard label="Active Users"   value={activeCount}   icon={<FiUserCheck size={15}/>}  color={C.green} colorL={C.greenL} loading={loading}/>
            <KpiCard label="Inactive Users" value={inactiveCount} icon={<FiUserX size={15}/>}     color={C.red}   colorL={C.redL}  loading={loading}/>
          </div>

          {/* ── TABLE CARD ── */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', overflow:'hidden' }}>

            {/* Toolbar */}
            <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}`, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <FiUsers size={14} style={{ color:C.navy, opacity:0.75 }}/>
                <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>All Users</span>
                {!loading && <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:20, fontSize:10.5, fontWeight:700 }}>{totalCount||users.length}</span>}
              </div>

              {/* Status filter pills */}
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                {[
                  { val:'', label:'All' },
                  { val:'active',   label:'Active',   color:C.green, bg:C.greenL, border:C.greenB },
                  { val:'inactive', label:'Inactive',  color:C.red,   bg:C.redL,   border:C.redB   },
                ].map(f => {
                  const isAct = filterStatus===f.val;
                  return (
                    <button key={f.val} onClick={() => setFilterStatus(f.val)}
                      style={{ padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s', border:`1.5px solid ${isAct?(f.border||C.navy):C.border}`, background:isAct?(f.bg||C.navyL):'#fff', color:isAct?(f.color||C.navy):C.t4 }}>
                      {f.label}
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div style={{ position:'relative', flex:1, minWidth:200, maxWidth:320, marginLeft:'auto' }}>
                <FiSearch size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
                <input type="text" placeholder="Search name, email, role..." value={search} onChange={e=>setSearch(e.target.value)}
                  style={{ width:'100%', height:36, paddingLeft:34, paddingRight:12, border:`1.5px solid ${C.border}`, borderRadius:9, fontSize:13, fontFamily:'inherit', outline:'none', background:'#F9FAFB', color:C.t1, boxSizing:'border-box', transition:'all 0.15s' }}
                  onFocus={e=>{e.target.style.borderColor=C.navy;e.target.style.background='#fff';e.target.style.boxShadow='0 0 0 3px rgba(13,59,94,0.08)';}}
                  onBlur={e=>{e.target.style.borderColor=C.border;e.target.style.background='#F9FAFB';e.target.style.boxShadow='none';}}/>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div style={{ padding:16, display:'flex', flexDirection:'column', gap:10 }}>
                {[1,2,3,4,5].map(i => <Sh key={i} h={52} r={8}/>)}
              </div>
            )}

            {/* API Error */}
            {!loading && apiError && (
              <div style={{ padding:'36px 24px', textAlign:'center' }}>
                <div style={{ display:'inline-block', maxWidth:520, textAlign:'left', background:C.redL, border:`1px solid ${C.redB}`, borderRadius:12, padding:'18px 22px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                    <FiAlertCircle size={16} style={{ color:C.red }}/>
                    <span style={{ fontWeight:700, color:C.red, fontSize:13 }}>Cannot Load Users</span>
                  </div>
                  <p style={{ color:'#7F1D1D', fontSize:12.5, margin:'0 0 14px', lineHeight:1.6 }}>{apiError}</p>
                  <button onClick={() => fetchAll()} style={{ background:C.red, color:'#fff', border:'none', borderRadius:8, padding:'7px 16px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Retry</button>
                </div>
              </div>
            )}

            {/* Empty */}
            {!loading && !apiError && filtered.length===0 && (
              <div style={{ padding:'52px 24px', textAlign:'center' }}>
                <div style={{ width:60, height:60, borderRadius:'50%', background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                  <FiUser size={24} style={{ color:C.navy, opacity:0.5 }}/>
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>{search?'No users match your search':'No users yet'}</div>
                <div style={{ fontSize:12.5, color:C.t3 }}>{search?'Try a different keyword':'Click "Add User" to create the first user'}</div>
              </div>
            )}

            {/* Table */}
            {!loading && !apiError && filtered.length>0 && (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {[['User','30%'],['Mobile','14%'],['Role','16%'],['Designation','16%'],['Status','12%'],['Actions','12%']].map(([col, w], i) => (
                        <th key={col} style={{ padding:'10px 16px', fontSize:10.5, fontWeight:800, color:C.t1, textTransform:'uppercase', letterSpacing:'0.08em', textAlign:i===5?'right':'left', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap', width:w }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((user, idx) => {
                      const fullName = `${user.first_name||''} ${user.last_name||''}`.trim();
                      const isDel = deletingId===user.id;
                      const isLast = idx===filtered.length-1;
                      return (
                        <tr key={user.id} className="ul-tr" style={{ background:C.white, opacity:isDel?0.5:1, transition:'all 0.12s' }}>
                          <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                              <Avatar firstName={user.first_name} lastName={user.last_name} image={user.profile_picture} size={34}/>
                              <div>
                                <div style={{ fontWeight:700, fontSize:13, color:C.t1, lineHeight:1.3 }}>{fullName||'—'}</div>
                                <div style={{ fontSize:11.5, color:C.t4, marginTop:2 }}>{user.email||'—'}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, fontFamily:'monospace', color:C.t2 }}>{user.mobile_number||<span style={{color:C.t4}}>—</span>}</td>
                          <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}><RoleBadge roleValue={user.role} roleMap={roleMap}/></td>
                          <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, color:C.t2 }}>{user.designation||<span style={{color:C.t4}}>—</span>}</td>
                          <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}><StatusPill active={user.is_active!==false}/></td>
                          <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', alignItems:'center' }}>
                              <button onClick={() => handleToggleActive(user)} disabled={isDel||togglingId===user.id}
                                style={{ width:36, height:20, borderRadius:10, border:'none', cursor:isDel||togglingId===user.id?'not-allowed':'pointer', position:'relative', transition:'all 0.2s', background: user.is_active!==false ? C.green : '#CBD5E1', flexShrink:0 }}>
                                <div style={{ position:'absolute', top:2, left: user.is_active!==false ? 18 : 2, width:16, height:16, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.2)', transition:'all 0.2s' }}/>
                              </button>
                              <button className="ul-icon-btn" onClick={() => navigate(`/users/${user.id}/edit`)} title="Edit" disabled={isDel} style={{ color:C.navy, borderColor:C.border }}>
                                <FiEdit2 size={13}/>
                              </button>
                              <button className="ul-icon-btn" onClick={() => setConfirmDel(user)} title="Delete" disabled={isDel} style={{ color:C.red, borderColor:C.redB }}>
                                {isDel
                                  ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/>
                                  : <FiTrash2 size={13}/>}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && totalCount>PAGE_SIZE && (
              <Pagination currentPage={currentPage} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={p => fetchAll(p)}/>
            )}

            {/* Footer */}
            {!loading && filtered.length>0 && (
              <div style={{ padding:'10px 16px', borderTop:`1px solid ${C.borderL}`, fontSize:11.5, color:C.t4, background:'#FAFBFC' }}>
                {search
                  ? `${filtered.length} result${filtered.length!==1?'s':''} matching "${search}" on this page`
                  : `Showing ${(currentPage-1)*PAGE_SIZE+1}–${Math.min(currentPage*PAGE_SIZE,totalCount)} of ${totalCount} user${totalCount!==1?'s':''}`
                }
              </div>
            )}
          </div>
        </div>
      </Layout>

      {/* ── DELETE CONFIRM MODAL ── */}
      {confirmDel && (
        <div style={{ position:'fixed', inset:0, background:'rgba(13,27,42,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:16 }}
          onClick={e=>{ if(e.target===e.currentTarget) setConfirmDel(null); }}>
          <div style={{ background:C.white, borderRadius:16, width:'100%', maxWidth:400, boxShadow:'0 24px 60px rgba(13,27,42,0.35)', overflow:'hidden', animation:'cfFadeUp 0.18s ease both' }}>
            <div style={{ padding:'18px 22px', borderBottom:`1px solid ${C.borderL}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:800, fontSize:15, color:C.t1 }}>Delete User</span>
              <button onClick={() => setConfirmDel(null)} style={{ background:'none', border:'none', cursor:'pointer', color:C.t4, display:'flex' }}><FiX size={18}/></button>
            </div>
            <div style={{ padding:'22px', textAlign:'center' }}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:C.redL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                <FiTrash2 size={22} style={{ color:C.red }}/>
              </div>
              <p style={{ fontSize:13.5, color:C.t2, margin:'0 0 6px', lineHeight:1.5 }}>
                Are you sure you want to delete <strong style={{ color:C.t1 }}>{`${confirmDel.first_name||''} ${confirmDel.last_name||''}`.trim()||confirmDel.email}</strong>?
              </p>
              <p style={{ fontSize:12, color:C.t4, margin:0 }}>This action cannot be undone.</p>
            </div>
            <div style={{ padding:'14px 22px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'flex-end', background:'#FAFBFC' }}>
              <button onClick={() => setConfirmDel(null)} style={{ height:38, padding:'0 20px', border:`1px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
              <button onClick={() => handleDelete(confirmDel)} style={{ height:38, padding:'0 22px', background:C.red, color:'#fff', border:'none', borderRadius:9, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6, boxShadow:'0 2px 8px rgba(220,38,38,0.35)' }}>
                <FiTrash2 size={13}/> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
